// /start/business — 3-step Find My Business wizard.
//   1. Search PBF directory by name (+ optional city/area)
//   2. Pick a match → orchestrator hydrates a Brief draft
//   3. Confirm subdomain, save brief, create site, kick initial build
//
// Submit redirects to /sites/:id where the live build timeline takes
// over.

import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, ApiError, type BusinessHit, type BriefDraft, type HarvestSummary, type SubdomainCheck } from "../../../lib/api";
import { CAPABILITIES } from "../../../lib/catalog";
import { CUSTOMER_ROOT_DOMAIN } from "../../../lib/domains";
import { ErrBox, SubdomainHint, suggestSubdomain, summarizeHours } from "../shared";
import {
  clearWizardDraft, clearWizardRequestId, getOrCreateWizardRequestId,
  loadWizardDraft, saveWizardDraft,
} from "../../../lib/wizardStorage";
import StartShell from "../../../routes/start/StartShell";
import UpgradeModal from "../../billing/UpgradeModal";
import { startProblem } from "../shared/startProblem";

type Step = 1 | 2 | 3;

/** The start API returns this (402) when the account is at its plan's site limit. */
function isSitePaywall(err: unknown): boolean {
  return err instanceof ApiError && err.status === 402 &&
    (err.payload as { code?: string } | null)?.code === "site_token_limit";
}

export default function FindMyBusiness() {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const [step, setStep] = useState<Step>(1);
  const [sitePaywalled, setSitePaywalled] = useState(false);

  // Step 1
  const [requestedBusiness] = useState(() => new URLSearchParams(routeLocation.search).get("business")?.slice(0, 300) || "");
  const [requestedPlaceId] = useState(() => new URLSearchParams(routeLocation.search).get("placeId")?.trim() || "");
  const [selectedProspect, setSelectedProspect] = useState<BusinessHit | null>(() => requestedPlaceId
    ? { harvestId: requestedPlaceId, name: requestedBusiness || "Selected business" }
    : null);
  const [name, setName] = useState(requestedBusiness);
  const [city, setCity] = useState("");
  const [searching, setSearching] = useState(false);
  const [matches, setMatches] = useState<BusinessHit[] | null>(null);
  const [searchState, setSearchState] = useState<"cached" | "live" | "partial" | "failed" | null>(null);
  const [searchErr, setSearchErr] = useState<string | null>(null);

  // Step 2 → 3
  const [picked, setPicked] = useState<BusinessHit | null>(null);
  const [prefilling, setPrefilling] = useState(false);
  const [brief, setBrief] = useState<BriefDraft | null>(null);
  const [summary, setSummary] = useState<HarvestSummary | null>(null);
  const [prefillErr, setPrefillErr] = useState<string | null>(null);

  // Step 3
  const [subdomain, setSubdomain] = useState("");
  const [subCheck, setSubCheck] = useState<SubdomainCheck | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [pendingBriefId, setPendingBriefId] = useState<string | null>(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [requestId] = useState(() => {
    if (requestedBusiness || requestedPlaceId) clearWizardRequestId("business");
    return getOrCreateWizardRequestId("business");
  });

  useEffect(() => {
    if (requestedBusiness || requestedPlaceId) {
      const query = new URLSearchParams(routeLocation.search);
      query.delete("business");
      query.delete("placeId");
      navigate({ pathname: routeLocation.pathname, search: query.toString(), hash: routeLocation.hash }, { replace: true });
      setDraftLoaded(true);
      return;
    }
    const draft = loadWizardDraft<any>("business");
    if (draft) {
      if (draft.step >= 1 && draft.step <= 3) setStep(draft.step);
      setName(draft.name ?? ""); setCity(draft.city ?? "");
      setMatches(Array.isArray(draft.matches) ? draft.matches : null);
      setSearchState(draft.searchState ?? null);
      setPicked(draft.picked ?? null); setBrief(draft.brief ?? null);
      setSummary(draft.summary ?? null); setSubdomain(draft.subdomain ?? "");
      setSelectedProspect(draft.selectedProspect ?? null);
      setPendingBriefId(typeof draft.pendingBriefId === "string" ? draft.pendingBriefId : null);
    }
    setDraftLoaded(true);
  }, []);

  useEffect(() => {
    if (!draftLoaded || submitting || prefilling || searching) return;
    saveWizardDraft("business", {
      step, name, city, matches, searchState, picked, brief, summary, subdomain, pendingBriefId, selectedProspect,
    });
  }, [
    draftLoaded, submitting, prefilling, searching, step, name, city, matches,
    searchState, picked, brief, summary, subdomain, pendingBriefId, selectedProspect,
  ]);

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setSearchErr(null);
    setMatches(null);
    setSelectedProspect(null);
    setSearching(true);
    try {
      const res = await api.searchBusinesses(name.trim(), city.trim() || undefined);
      setMatches(res.matches);
      setSearchState(res.state);
    } catch (err: any) {
      if (isSitePaywall(err)) { setSitePaywalled(true); return; }
      setSearchState("failed");
      setSearchErr(err?.payload?.error || "Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  }

  async function pickMatch(hit: BusinessHit) {
    setPicked(hit);
    setPrefillErr(null);
    setPrefilling(true);
    setStep(2);
    try {
      const res = await api.prefillFromBusiness(hit.harvestId);
      setBrief(res.brief);
      setSummary(res.harvestSummary);
      setSubdomain(suggestSubdomain(res.brief.business.name || hit.name));
      setStep(3);
    } catch (err: any) {
      if (isSitePaywall(err)) { setSitePaywalled(true); return; }
      setPrefillErr(err?.payload?.error || "Could not load business details. Try another match.");
      setStep(1);
    } finally {
      setPrefilling(false);
    }
  }

  // Live subdomain availability check (debounced).
  useEffect(() => {
    if (step !== 3) return;
    const sub = subdomain.trim();
    if (!sub) { setSubCheck(null); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.checkSubdomain(sub)
        .then((r) => { if (!cancelled) setSubCheck(r); })
        .catch(() => { if (!cancelled) setSubCheck(null); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [subdomain, step]);

  async function handleSubmit() {
    if (!brief) return;
    if (!subCheck?.available) return;
    setSubmitErr(null);
    setSubmitting(true);
    try {
      const briefId = pendingBriefId ?? (await api.createBrief({
        source: "pbf",
        business: brief.business,
        capabilities: brief.capabilities ?? [],
        integrations: brief.integrations,
        inspo: brief.inspo ?? [],
        attachments: brief.attachments,
        ownerNote: brief.ownerNote,
        pbfHarvestId: brief.pbfHarvestId,
        voice: brief.voice,
      })).brief.id;
      if (!pendingBriefId) setPendingBriefId(briefId);
      const siteRes = await api.startNewSite({
        subdomain: subdomain.trim().toLowerCase(),
        briefId,
        kickBuild: true,
        requestId,
      });
      clearWizardDraft("business");
      clearWizardRequestId("business");
      // Send the customer straight to the friendly build-progress page
      // when we got a jobId back. Falls back to the site detail page
      // (which has a build-in-flight link) if the orchestrator didn't
      // return one.
      if (siteRes.jobId) {
        navigate(`/sites/${siteRes.site.id}/builds/${siteRes.jobId}`);
      } else {
        navigate(`/sites/${siteRes.site.id}`);
      }
    } catch (err: any) {
      if (isSitePaywall(err)) { setSitePaywalled(true); setSubmitting(false); return; }
      setSubmitErr(startProblem(err).message);
      setSubmitting(false);
    }
  }

  return (
    <StartShell
      title="Find my business"
      subtitle="We'll pull your business info from Google. Less typing, less waiting."
      step={{ current: step, total: 3 }}
    >
      {step === 1 && (
        <form onSubmit={handleSearch} className="stack" style={{ gap: 16 }}>
          {selectedProspect && (
            <div className="card stack" style={{ gap: 10 }}>
              <strong>Selected business: {selectedProspect.name}</strong>
              <p>Continue with this business to review its details, or search for another below.</p>
              <button type="button" className="btn primary" onClick={() => void pickMatch(selectedProspect)}>
                Use selected business
              </button>
            </div>
          )}
          {prefillErr && <ErrBox>{prefillErr}</ErrBox>}
          <div>
            <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
              What's your business called?
            </label>
            <input
              autoFocus
              required
              minLength={2}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Asheville Pizza Kitchen"
              style={{ marginTop: 6 }}
            />
          </div>
          <div>
            <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
              City / area (optional)
            </label>
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. Asheville, NC"
              style={{ marginTop: 6 }}
            />
          </div>
          <button
            type="submit"
            className="btn primary"
            disabled={searching || name.trim().length < 2}
            style={{ alignSelf: "flex-start" }}
          >
            {searching ? "Searching…" : "Search Google"}
          </button>
          {searchErr && (
            <ErrBox>
              {searchErr} You can retry this search or{" "}
              <a href="/start/scratch" style={{ color: "inherit", fontWeight: 700 }}>build from scratch</a>.
            </ErrBox>
          )}
          {searchState === "partial" && !searchErr && (
            <div className="muted" style={{ padding: 12, background: "#fffbeb", borderRadius: 8 }}>
              Some sources timed out. These results are usable; retry for more, or build from scratch.
            </div>
          )}
          {matches !== null && matches.length === 0 && !searchErr && (
            <div className="muted" style={{ padding: 14, background: "#f3f4f6", borderRadius: 8 }}>
              No matches found. Try a different name, or skip ahead and{" "}
              <a href="/start/scratch" style={{ color: "var(--primary)", fontWeight: 600 }}>
                build from scratch
              </a>.
            </div>
          )}
          {matches && matches.length > 0 && (
            <div className="stack" style={{ gap: 8, marginTop: 8 }}>
              <div className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
                {matches.length} {matches.length === 1 ? "match" : "matches"} — pick yours:
              </div>
              {matches.map((m) => (
                <button
                  key={m.harvestId}
                  type="button"
                  onClick={() => pickMatch(m)}
                  className="match-card"
                >
                  <div style={{ fontWeight: 600 }}>{m.name}</div>
                  {m.address && <div className="muted" style={{ fontSize: 13 }}>{m.address}</div>}
                  <div className="muted" style={{ fontSize: 12, marginTop: 4, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {m.hasPhotos && <span>📷 photos</span>}
                    {m.hasMenu && <span>📋 menu</span>}
                    {m.harvestState === "cached" && <span style={{ color: "#6b7280" }}>cached details</span>}
                    {m.pendingHarvest && <span style={{ color: "#92400e" }}>live match · details pending</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </form>
      )}

      {step === 2 && (
        <div className="stack" style={{ gap: 16 }}>
          <div className="muted">
            {picked?.pendingHarvest ? "Harvesting live business details" : "Loading cached business details"} for{" "}
            <strong>{picked?.name}</strong>…
          </div>
          {prefilling && <div style={{ height: 4, background: "#e5e7eb", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", width: "60%", background: "var(--primary)", animation: "pulse 1.4s ease-in-out infinite" }} />
          </div>}
          {prefillErr && <ErrBox>{prefillErr}</ErrBox>}
        </div>
      )}

      {step === 3 && brief && (
        <div className="stack" style={{ gap: 18 }}>
          <HarvestReview brief={brief} summary={summary} />

          <div>
            <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
              Pick your protopal.ai address
            </label>
            <div className="row" style={{ marginTop: 6, gap: 0 }}>
              <input
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
                placeholder="your-business"
                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              />
              <span style={{
                padding: "8px 12px",
                border: "1px solid var(--border)",
                borderLeft: "none",
                borderTopRightRadius: "var(--radius)",
                borderBottomRightRadius: "var(--radius)",
                background: "#f9fafb",
                color: "var(--muted)",
                whiteSpace: "nowrap",
              }}>.{CUSTOMER_ROOT_DOMAIN}</span>
            </div>
            <SubdomainHint check={subCheck} subdomain={subdomain} />
          </div>

          <div className="row" style={{ gap: 10 }}>
            <button
              className="btn primary"
              onClick={handleSubmit}
              disabled={submitting || !subCheck?.available}
            >
              {submitting ? "Starting your site…" : "Start building →"}
            </button>
            <button type="button" className="btn" onClick={() => setStep(1)} disabled={submitting}>
              ← Search again
            </button>
          </div>
          {submitErr && <ErrBox>{submitErr}</ErrBox>}
          <div className="muted" style={{ fontSize: 12 }}>
            We'll build your draft in the background and show you its progress. You can review and edit it before publishing.
          </div>
        </div>
      )}

      <UpgradeModal open={sitePaywalled} reason="site" onClose={() => setSitePaywalled(false)} />
    </StartShell>
  );
}


function HarvestReview({ brief, summary }: { brief: BriefDraft; summary: HarvestSummary | null }) {
  const photos = brief.attachments?.filter((a) => a.source === "pbf-harvest") ?? [];
  const photoCount = photos.length;
  const signals = brief.business.signals ?? {};
  const services = signals.services ?? {};
  const serviceList = Object.entries(services).filter(([, v]) => v).map(([k]) => k);
  const reviewCount = signals.reviewCount;
  const rating = signals.rating;
  const capLabels = (brief.capabilities ?? [])
    .map((id) => CAPABILITIES.find((c) => c.id === id)?.label)
    .filter(Boolean) as string[];

  return (
    <div className="card" style={{ background: "#ecfdf5", borderColor: "#a7f3d0", padding: 0 }}>
      <div style={{ padding: "14px 16px", borderBottom: "1px solid #a7f3d0" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#065f46", marginBottom: 4 }}>
          {summary?.state === "live" ? "✓ Business details found" : "✓ Saved business details"}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700 }}>{brief.business.name}</div>
        {brief.business.contact?.address && (
          <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{brief.business.contact.address}</div>
        )}
      </div>

      <div style={{ padding: "12px 16px" }}>
        <FactRow label="Details found" value={
          [
            photoCount ? `${photoCount} photo${photoCount === 1 ? "" : "s"}` : null,
            summary?.hasMenu ? "menu" : null,
            brief.business.hours && Object.keys(brief.business.hours).length ? "opening hours" : null,
            rating ? `★ ${rating.toFixed(1)} · ${reviewCount ?? 0} reviews` : null,
          ].filter(Boolean).join(" · ") || "Business details"
        } />
        <FactRow label="Description" value={
          brief.business.description.length > 220
            ? brief.business.description.slice(0, 220) + "…"
            : brief.business.description
        } />
        {brief.business.contact?.phone && (
          <FactRow label="Phone" value={brief.business.contact.phone} />
        )}
        {brief.business.hours && Object.keys(brief.business.hours).length > 0 && (
          <FactRow label="Hours" value={summarizeHours(brief.business.hours)} />
        )}
        {capLabels.length > 0 && (
          <FactRow label="Site sections" value={capLabels.join(", ")} />
        )}
        {serviceList.length > 0 && (
          <FactRow label="Services Google has on file" value={
            serviceList.slice(0, 6).join(", ") + (serviceList.length > 6 ? `, +${serviceList.length - 6} more` : "")
          } />
        )}
      </div>

      <div style={{
        padding: "10px 16px",
        background: "#d1fae5",
        borderTop: "1px solid #a7f3d0",
        fontSize: 12,
        color: "#065f46",
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
      }}>
        We'll use your business details and suitable photos to create a complete first draft. Review and edit anything before publishing.
      </div>
    </div>
  );
}

function FactRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ padding: "6px 0", borderBottom: "1px solid #d1fae5", display: "flex", gap: 12 }}>
      <div style={{
        minWidth: 110, fontSize: 11, fontWeight: 700, color: "#065f46",
        textTransform: "uppercase", letterSpacing: 0.04, paddingTop: 2,
      }}>
        {label}
      </div>
      <div style={{ flex: 1, fontSize: 14, color: "#064e3b", lineHeight: 1.5 }}>{value}</div>
    </div>
  );
}
